import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Loader2 } from 'lucide-react'
import {
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type {
	CommitmentMatrixEvolutionPoint,
	CommitmentMatrixEvolutionRow,
	CommitmentMatrixEvolutionSortColumn,
	CommitmentMatrixEvolutionTotalPoint,
} from '@/types'
import { formatPercentage } from '@/utils/format-percentage'
import {
	pillClassForPct,
	STATUS_LABEL,
	STATUS_PILL_CLASS,
} from './collaborator-status'
import { SortableHead, type SortState } from './sortable-head'

const IDENTITY_COLUMNS_COUNT = 4
const PILL_CLASS = 'inline-flex rounded-full px-2 py-0.5 text-xs font-semibold'

function formatMonth(month: string): string {
	const [year, monthIndex] = month.split('-').map(Number)
	return format(new Date(year, monthIndex - 1, 1), 'MMM/yy', { locale: ptBR })
}

interface CollaboratorsEvolutionTableProps {
	months: string[]
	items: CommitmentMatrixEvolutionRow[]
	scopeTotal: CommitmentMatrixEvolutionTotalPoint[]
	isFetching: boolean
	sort: SortState<CommitmentMatrixEvolutionSortColumn>
	onSort: (column: CommitmentMatrixEvolutionSortColumn) => void
	onSelectPerson: (personId: string) => void
}

export function CollaboratorsEvolutionTable({
	months,
	items,
	scopeTotal,
	isFetching,
	sort,
	onSort,
	onSelectPerson,
}: CollaboratorsEvolutionTableProps) {
	return (
		<div className="relative overflow-x-auto rounded-lg border border-kaefer-light-silver">
			{isFetching && (
				<div className="absolute inset-0 z-10 flex items-start justify-center bg-white/60 pt-10">
					<Loader2 className="size-5 animate-spin text-kaefer-medium-grey" />
				</div>
			)}
			<Table className={cn(isFetching && 'pointer-events-none opacity-50')}>
				<TableHeader>
					<TableRow className="bg-muted/30">
						<SortableHead column="registration" sort={sort} onSort={onSort}>
							Matrícula
						</SortableHead>
						<SortableHead column="name" sort={sort} onSort={onSort}>
							Nome
						</SortableHead>

						<TableHead>Grupo</TableHead>
						<SortableHead column="branchName" sort={sort} onSort={onSort}>
							Filial
						</SortableHead>
						{months.map((month) => (
							<TableHead key={month} className="text-center capitalize">
								{formatMonth(month)}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{items.length === 0 ? (
						<TableRow>
							<TableCell
								colSpan={IDENTITY_COLUMNS_COUNT + months.length}
								className="py-10 text-center text-sm text-muted-foreground whitespace-normal"
							>
								Nenhum colaborador encontrado para os filtros selecionados.
							</TableCell>
						</TableRow>
					) : (
						items.map((c) => (
							<TableRow
								key={c.personId}
								className="cursor-pointer hover:bg-muted/40"
								onClick={() => onSelectPerson(c.personId)}
							>
								<TableCell className="text-muted-foreground">
									{c.registration ?? '—'}
								</TableCell>
								<TableCell className="font-medium text-kaefer-dark-grey">
									{c.name}
								</TableCell>

								<TableCell className="text-muted-foreground">
									{c.clusterName ?? c.jobGroupName ?? '—'}
								</TableCell>
								<TableCell className="text-muted-foreground">
									{c.branchName}
								</TableCell>
								{c.months.map((point) => (
									<TableCell key={point.month} className="text-center">
										<MonthCell point={point} />
									</TableCell>
								))}
							</TableRow>
						))
					)}
				</TableBody>
				{items.length > 0 && (
					<TableFooter>
						<TableRow>
							<TableCell
								colSpan={IDENTITY_COLUMNS_COUNT}
								className="font-semibold text-kaefer-dark-grey"
							>
								Total do escopo
							</TableCell>
							{scopeTotal.map((point) => (
								<TableCell key={point.month} className="text-center">
									<TotalCell point={point} />
								</TableCell>
							))}
						</TableRow>
					</TableFooter>
				)}
			</Table>
		</div>
	)
}

function MonthCell({ point }: { point: CommitmentMatrixEvolutionPoint }) {
	if (point.status === 'no_target') {
		return <span className="text-muted-foreground">—</span>
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span className={cn(PILL_CLASS, STATUS_PILL_CLASS[point.status])}>
					{point.status === 'aboned'
						? 'Abonado'
						: formatPercentage(point.adherencePct)}
				</span>
			</TooltipTrigger>
			<TooltipContent side="top" className="text-left">
				<p className="font-semibold">{STATUS_LABEL[point.status]}</p>
				<p>
					<span className="text-muted-foreground">Progresso:</span> {point.done}
					/{point.target}
				</p>
				{point.hasException && (
					<p>
						<span className="text-muted-foreground">Meta original:</span>{' '}
						{point.originalTarget} (ajustada por exceção)
					</p>
				)}
			</TooltipContent>
		</Tooltip>
	)
}

function TotalCell({ point }: { point: CommitmentMatrixEvolutionTotalPoint }) {
	if (point.adherencePct === null) {
		return <span className="text-muted-foreground">—</span>
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span className={cn(PILL_CLASS, pillClassForPct(point.adherencePct))}>
					{formatPercentage(point.adherencePct)}
				</span>
			</TooltipTrigger>
			<TooltipContent side="top" className="text-left">
				<p>
					<span className="text-muted-foreground">Realizado:</span> {point.done}
				</p>
				<p>
					<span className="text-muted-foreground">Meta:</span> {point.target}
					{point.originalTarget !== point.target &&
						` (original ${point.originalTarget})`}
				</p>
			</TooltipContent>
		</Tooltip>
	)
}
